/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CarouselRef } from 'antd/es/carousel';
import { Carousel } from 'antd-v5';
import styled from 'styled-components';
import { RootState } from 'app/AppStore';
import { Box3dIcon, ZoomInOutlinedIcon } from 'assets/icons';
import { DiscountTag, FlashCertificate } from 'components/Discount';
import GLViewerWrapper from 'domains/3d/GLViewerWrapper';
import { getIsStockAvailable } from 'domains/references';
import { GarageView, SparePartsViewType } from 'domains/user/User.types';
import { ProductImage } from 'pages/CatalogPage/DH/Product/ProductImageCard/ProductImage';
import { ProductThumbnailCard } from 'pages/CatalogPage/DH/Product/ProductThumbnailCard/ProductThumbnailCard';
import { BrandImage } from 'pages/CatalogPage/DH/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/BrandImage';
import { TireBrandImage } from 'pages/TiresPage/TireReferencesSection/TireReferenceCard/TireBrandImage';
import { theme } from 'styles';
import { BlackButton, Box, CenteredSpin, Flex, Icon, MarginBox, Text } from 'UI';
import { ProductGalleryAsset, ProductGalleryState } from './ProductGallery.types';
import {
  BrandContainer,
  DiscontinuedOverlay,
  DiscontinuedText,
  GalleryContainer,
  GallerySlide,
  GalleryViewport,
  ImageZoomTrigger,
  PromotionContainer,
  ThreeDPlaceholder,
  ThreeDViewerContainer,
  ThumbnailButton,
  ThumbnailRow,
  ZoomButton,
} from './ProductImageGallery.styled';

const IMAGE_3D = '3d/3d_view.png';

interface ProductImageGalleryProps {
  gallery: ProductGalleryState;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  sparePartsView: SparePartsViewType;
  isDiscontinued?: boolean;
  expanded?: boolean;
  thumbnailsVertical?: boolean;
  hideThumbnails?: boolean;
  thumbnailsOnly?: boolean;
  onOpenExpanded: (index: number) => void;
}

const CarouselAnt = styled(Carousel)`
  .slick-prev,
  .slick-next,
  .slick-prev:hover,
  .slick-next:hover {
    color: ${theme.color.brand_black};
  }
  position: relative;
  width: 100%;

  .ant5-carousel .slick-dots,
  .slick-dots {
    bottom: 12px;

    li {
      width: 18px;
      height: 4px;
    }

    li button {
      width: 18px;
      height: 4px;
      border-radius: 0;
      background: ${theme.color.grey_metal};
      opacity: 1;
    }

    li.slick-active {
      width: 24px;
    }

    li.slick-active button {
      width: 24px;
      background: ${theme.color.brand_black};
    }
  }
`;

const fallbackAsset: ProductGalleryAsset = { id: 'fallback-product-image', type: 'image' };

export const ProductImageGallery = ({
  gallery,
  selectedIndex,
  setSelectedIndex,
  sparePartsView,
  isDiscontinued,
  expanded,
  thumbnailsVertical,
  hideThumbnails,
  thumbnailsOnly,
  onOpenExpanded,
}: ProductImageGalleryProps) => {
  const { t } = useTranslation();
  const carouselRef = useRef<CarouselRef>(null);
  const hasAssets = gallery.assets.length > 0;
  const assets = hasAssets ? gallery.assets : [fallbackAsset];
  const safeSelectedIndex = Math.min(selectedIndex, assets.length - 1);
  const selectedAsset = assets[safeSelectedIndex];
  const isSelected3d = selectedAsset?.type === '3d';
  const isExpanded3d = expanded && isSelected3d;
  const showControls = assets.length > 1;
  const showCarouselControls = showControls && !isExpanded3d;
  const availableInUserCountry = useSelector((state: RootState) => getIsStockAvailable(state, gallery.referenceNumber));

  useEffect(() => {
    carouselRef.current?.goTo(safeSelectedIndex, false);
  }, [safeSelectedIndex]);

  const handleSelectAsset = (index: number) => {
    setSelectedIndex(index);
    carouselRef.current?.goTo(index);
  };

  const handleOpenExpanded = (index = safeSelectedIndex) => {
    if (assets[index]?.type === '3d') {
      gallery.load3dModel();
    }
    onOpenExpanded(index);
  };

  const renderPromoCorner = () => {
    if (sparePartsView !== GarageView) {
      return null;
    }

    return (
      <>
        <FlashCertificate reference={gallery.referenceNumber} type={'product'} />
        <DiscountTag reference={gallery.referenceNumber} />
      </>
    );
  };

  const renderBrand = () => {
    if (selectedAsset?.type === '3d') {
      return null;
    }

    return gallery.tireBrandId ? (
      <TireBrandImage referenceBrandId={gallery.tireBrandId} renative={gallery.renative} width={150} height={45} />
    ) : (
      <BrandImage
        vehicleBrand={gallery.vehicleBrand}
        referenceBrand={gallery.referenceBrand}
        familyCode={gallery.familyCode}
        renative={gallery.renative}
        size={120}
        usePng={expanded}
      />
    );
  };

  const render3dPlaceholder = () => (
    <ThreeDPlaceholder direction={'column'} align={'center'} justify={'center'} $expanded={expanded}>
      {gallery.loading3d && (
        <Box height={80} width={80}>
          <CenteredSpin size={'large'} />
        </Box>
      )}
      <ProductImage
        imageUrl={IMAGE_3D}
        catalogSource={gallery.catalogSource}
        alt={t('catalog.3d_view.3d_view', '3D view')}
        availableInUserCountry
        localImage
        maxImageHeight={300}
      />
      <MarginBox mt={20} />
      <Text type={'h2'}>{t('catalog.3d_view.view_3d_view_available', '3D View available')}</Text>
      <MarginBox mt={20} />
      <Flex size={0}>
        <BlackButton onClick={() => handleOpenExpanded(safeSelectedIndex)} size={'xlarge'}>
          {t('catalog.3d_view.view_3d_view', 'View 3D view')}
        </BlackButton>
      </Flex>
    </ThreeDPlaceholder>
  );

  const renderImageAsset = (asset: ProductGalleryAsset, index: number) => {
    const productImage = (
      <ProductImage
        imageUrl={asset.imageKey}
        catalogSource={gallery.catalogSource}
        alt={`reference-${gallery.referenceNumber}`}
        availableInUserCountry={availableInUserCountry}
        maxImageHeight={expanded ? 560 : 450}
      />
    );

    if (expanded) {
      return productImage;
    }

    return (
      <ImageZoomTrigger
        aria-label={t('catalog.product.image.open_expanded_view', 'Open expanded image view')}
        data-testid={`product-gallery-image-zoom-trigger-${index}`}
        onClick={() => handleOpenExpanded(index)}
        type={'button'}
      >
        {productImage}
      </ImageZoomTrigger>
    );
  };

  const renderAsset = (asset: ProductGalleryAsset, index: number) => {
    if (asset.type === '3d') {
      if (expanded) {
        return (
          <ThreeDViewerContainer>
            {gallery.json ? <GLViewerWrapper jsonString={gallery.json} /> : <CenteredSpin size={'large'} />}
          </ThreeDViewerContainer>
        );
      }
      return render3dPlaceholder();
    }

    return renderImageAsset(asset, index);
  };

  const renderThumbnails = () => {
    if (hideThumbnails || gallery.hasOnly3dAsset || assets.length <= 1) {
      return null;
    }

    return (
      <ThumbnailRow direction={'row'} $vertical={thumbnailsVertical}>
        {assets.map((asset, index) =>
          asset.type === '3d' ? (
            <ThumbnailButton
              key={asset.id}
              $active={index === safeSelectedIndex}
              onClick={() => handleSelectAsset(index)}
              data-testid={'product-gallery-3d-thumbnail'}
            >
              <Icon IconComponent={Box3dIcon} size={20} color={theme.color.brand_black} noPointer />
              <Text type={'dark_12'}>{t('catalog.3d_view.view_3d_view', '3D view')}</Text>
            </ThumbnailButton>
          ) : (
            <ProductThumbnailCard
              key={asset.id}
              imageKey={asset.imageKey ?? ''}
              active={index === safeSelectedIndex}
              onClick={() => handleSelectAsset(index)}
              index={index}
              catalogSource={gallery.catalogSource}
            />
          ),
        )}
      </ThumbnailRow>
    );
  };

  if (thumbnailsOnly) {
    return <>{renderThumbnails()}</>;
  }
  return (
    <GalleryContainer
      $expanded={expanded}
      data-testid={expanded ? 'product-expanded-gallery' : 'product-image-gallery'}
    >
      <BrandContainer>{renderBrand()}</BrandContainer>
      <GalleryViewport $expanded={expanded}>
        <div className={'product-image-gallery-carousel'}>
          <CarouselAnt
            arrows={showCarouselControls}
            ref={carouselRef}
            dots={showCarouselControls}
            infinite={showCarouselControls}
            afterChange={setSelectedIndex}
            draggable={showCarouselControls}
          >
            {assets.map((asset, index) => (
              <GallerySlide
                key={asset.id}
                align={'center'}
                justify={'center'}
                $hasBackground={asset.type === '3d'}
                $expanded={expanded}
                $hasAssets={hasAssets}
              >
                {renderAsset(asset, index)}
              </GallerySlide>
            ))}
          </CarouselAnt>
        </div>
        {!expanded && (
          <ZoomButton onClick={() => handleOpenExpanded()}>
            <Icon IconComponent={ZoomInOutlinedIcon} size={18} color={theme.color.brand_black} />
          </ZoomButton>
        )}
        {isDiscontinued && !isExpanded3d && (
          <DiscontinuedOverlay>
            <DiscontinuedText>{t('catalog.product.image.discontinued', 'DISCONTINUED')}</DiscontinuedText>
          </DiscontinuedOverlay>
        )}
      </GalleryViewport>
      {!isSelected3d && <PromotionContainer>{renderPromoCorner()}</PromotionContainer>}
      {renderThumbnails()}
    </GalleryContainer>
  );
};
